// Check if CartDrawer has already been defined to prevent duplicate declaration
// console.log('CartDrawer script loading...');
if (typeof window.CartDrawer === 'undefined') {
  // console.log('CartDrawer class not found, creating new instance...');
  window.CartDrawer = class {
    constructor() {
      this.drawer = document.querySelector('.cart-drawer');
      this.cartToggle = document.querySelector('.js-cart-toggle');
      this.addToCartForm = document.querySelector('.cart-drawer__add-to-cart-form');
      this.cartOverlay = document.querySelector('.cart-overlay');
      this.closeButton = document.querySelector('#cart-drawer-close');
      this.errorNotification = document.querySelector('.cart-drawer__error-notification');
      this.errorMessage = document.querySelector('.cart-drawer__error-message');
      this.recommendationsTrack = document.querySelector('.cart-drawer__recommendations-track');
      this.prevButton = document.querySelector('.cart-drawer__carousel-button.cart-drawer__carousel-prev');
      this.nextButton = document.querySelector('.cart-drawer__carousel-button.cart-drawer__carousel-next');
      
      // Initialize Auto Gift Manager
      this.initializeAutoGiftManager();
      
      this.bindEvents();
      this.setupCarousel();
      
      
      // Check if gift box is in cart when drawer is initialized
      this.initializeGiftBoxState();
    }

    // Initialize Auto Gift Manager with cart drawer integration
    initializeAutoGiftManager() {
      console.log('🎁 [Auto Gift] Initializing Auto Gift Manager...');
      
      // Load gift rules from settings
      const threshold = window.cartDrawerSettings?.goalProduct1Threshold || 0;
      const threshold2 = window.cartDrawerSettings?.goalProduct2Threshold || 0;
      const threshold3 = window.cartDrawerSettings?.goalProduct3Threshold || 0;
      const freeProductVariantId1 = window.cartDrawerSettings?.freeProductVariantId1;
      const freeProductVariantId2 = window.cartDrawerSettings?.freeProductVariantId2;
      const freeProductVariantId3 = window.cartDrawerSettings?.freeProductVariantId3;
      
      console.log('🎁 [Auto Gift] Settings:', {
        threshold: threshold,
        threshold2: threshold2,
        threshold3: threshold3,
        freeProductVariantId1: freeProductVariantId1,
        freeProductVariantId2: freeProductVariantId2,
        freeProductVariantId3: freeProductVariantId3,
        cartDrawerSettings: window.cartDrawerSettings
      });
      
      // Build rules array for all available thresholds and variant IDs
      const rules = { cartValue: [] };
      
      // Add rule for threshold 1
      if (threshold > 0 && freeProductVariantId1) {
        rules.cartValue.push({
          id: 'cart_drawer_free_gift_1',
          name: 'Free Gift on Cart Threshold 1',
          minimumValue: threshold / 100, // Convert cents to dollars
          giftProductId: freeProductVariantId1,
          quantity: 1,
          enabled: true,
          property: '_goal_product',
          propertyValue: 'true'
        });
      }
      
      // Add rule for threshold 2
      if (threshold2 > 0 && freeProductVariantId2) {
        rules.cartValue.push({
          id: 'cart_drawer_free_gift_2',
          name: 'Free Gift on Cart Threshold 2',
          minimumValue: threshold2 / 100, // Convert cents to dollars
          giftProductId: freeProductVariantId2,
          quantity: 1,
          enabled: true,
          property: '_goal_product',
          propertyValue: 'true'
        });
      }
      
      // Add rule for threshold 3
      if (threshold3 > 0 && freeProductVariantId3) {
        rules.cartValue.push({
          id: 'cart_drawer_free_gift_3',
          name: 'Free Gift on Cart Threshold 3',
          minimumValue: threshold3 / 100, // Convert cents to dollars
          giftProductId: freeProductVariantId3,
          quantity: 1,
          enabled: true,
          property: '_goal_product',
          propertyValue: 'true'
        });
      }
      
      console.log('🎁 [Auto Gift] Rules configured:', rules);
      
      if (rules.cartValue.length === 0) {
        console.log('🎁 [Auto Gift] No rules configured - no valid thresholds or variant IDs found');
      }

      // Create a simplified Auto Gift Manager specifically for cart drawer
      this.autoGiftManager = {
        rules: rules,
        cartState: null,
        isProcessing: false,

        // Check and apply gift rules
        async processCartUpdate() {
          console.log('🎁 [Auto Gift] Processing cart update...');
          
          if (this.isProcessing) {
            console.log('🎁 [Auto Gift] Already processing, skipping...');
            return;
          }
          
          this.isProcessing = true;
          console.log('🎁 [Auto Gift] Starting cart update process...');
          
          try {
            await this.updateCartState();
            await this.applyCartValueRules();
          } catch (error) {
            console.error('🎁 [Auto Gift] Error processing auto gift:', error);
          } finally {
            this.isProcessing = false;
            console.log('🎁 [Auto Gift] Cart update process completed');
          }
        },

        // Update cart state
        async updateCartState() {
          console.log('🎁 [Auto Gift] Updating cart state...');
          try {
            const response = await fetch('/cart.js');
            if (response.ok) {
              this.cartState = await response.json();
              console.log('🎁 [Auto Gift] Cart state updated:', {
                totalPrice: this.cartState.total_price,
                itemCount: this.cartState.item_count,
                items: this.cartState.items.map(item => ({
                  id: item.product_id,
                  variant_id: item.variant_id,
                  quantity: item.quantity,
                  properties: item.properties
                }))
              });
            } else {
              console.error('🎁 [Auto Gift] Failed to fetch cart:', response.status, response.statusText);
            }
          } catch (error) {
            console.error('🎁 [Auto Gift] Error updating cart state:', error);
          }
        },

        // Apply cart value rules
        async applyCartValueRules() {
          console.log('🎁 [Auto Gift] Applying cart value rules...');
          
          if (!this.cartState || !this.cartState.items) {
            console.log('🎁 [Auto Gift] No cart state or items, skipping rules');
            return;
          }

          // Check if cart has only unshippable products
          const hasShippableProduct = this.cartState.items.some(item => item.requires_shipping);
          const hasUnshippableProduct = this.cartState.items.some(item => !item.requires_shipping);
          
          // If cart has only unshippable products, calculate total from shippable items only
          // Otherwise, use regular cart total
          let cartTotal;
          if (!hasShippableProduct && hasUnshippableProduct) {
            cartTotal = this.cartState.items
              .filter(item => item.requires_shipping)
              .reduce((total, item) => total + (item.final_line_price / 100), 0);
            console.log('🎁 [Auto Gift] Cart has only unshippable products, using shippable-only total:', cartTotal);
          } else {
            cartTotal = this.cartState.total_price / 100; // Convert from cents
            console.log('🎁 [Auto Gift] Cart has mixed or only shippable products, using regular total:', cartTotal);
          }
          
          console.log('🎁 [Auto Gift] Cart total:', cartTotal, 'Rules to check:', this.rules.cartValue.length);
          
          // First, remove any gift products that are no longer configured in settings
          await this.removeUnconfiguredGiftProducts();
          
          for (const rule of this.rules.cartValue) {
            console.log('🎁 [Auto Gift] Checking rule:', {
              id: rule.id,
              name: rule.name,
              minimumValue: rule.minimumValue,
              giftProductId: rule.giftProductId,
              property: rule.property
            });
            
            const hasGift = this.hasGiftInCart(rule.giftProductId, rule.property);
            console.log('🎁 [Auto Gift] Has gift in cart:', hasGift);
            
            if (cartTotal >= rule.minimumValue && !hasGift) {
              console.log(`%c 🎁 [Auto Gift] Cart meets threshold (${cartTotal} >= ${rule.minimumValue}), adding gift product ${rule.giftProductId}...`, 'color: green; font-weight: bold;');
              await this.addGiftToCart(rule.giftProductId, rule.quantity, rule);
            } else if (cartTotal < rule.minimumValue && hasGift) {
              console.log(`%c 🎁 [Auto Gift] Cart below threshold (${cartTotal} < ${rule.minimumValue}), removing gift product ${rule.giftProductId}...`, 'color: red; font-weight: bold;');
              await this.removeGiftFromCart(rule.giftProductId, rule.property);
            } else {
              console.log(`%c 🎁 [Auto Gift] No action needed for gift product ${rule.giftProductId} (cart: ${cartTotal}, threshold: ${rule.minimumValue}, hasGift: ${hasGift})`, 'color: orange; font-weight: bold;');
            }
          }
        },

        // Remove gift products that are no longer configured in settings
        async removeUnconfiguredGiftProducts() {
          console.log('🎁 [Auto Gift] Checking for unconfigured gift products...');
          
          // Get all configured gift product variant IDs
          const configuredVariantIds = this.rules.cartValue.map(rule => parseInt(rule.giftProductId));
          console.log('🎁 [Auto Gift] Configured variant IDs:', configuredVariantIds);
          
          // Find all gift products in cart that are not in the configured list
          const unconfiguredGifts = this.cartState.items.filter(item => 
            item.properties && 
            item.properties._goal_product === 'true' && 
            !configuredVariantIds.includes(item.variant_id)
          );
          
          console.log('🎁 [Auto Gift] Unconfigured gift products found:', unconfiguredGifts.length);
          
          // Remove each unconfigured gift product
          for (const giftItem of unconfiguredGifts) {
            console.log(`%c 🎁 [Auto Gift] Removing unconfigured gift product ${giftItem.variant_id}...`, 'color: red; font-weight: bold;');
            await this.removeGiftFromCart(giftItem.variant_id, '_goal_product');
          }
        },

        // Check if gift is in cart
        hasGiftInCart(giftProductId, property) {
          const hasGift = this.cartState.items.some(item => {
            const matches = item.variant_id === parseInt(giftProductId) && 
              item.properties && 
              item.properties[property] === 'true';
            
            if (matches) {
              console.log('🎁 [Auto Gift] Found gift item:', {
                product_id: item.product_id,
                variant_id: item.variant_id,
                properties: item.properties
              });
            }
            
            return matches;
          });
          
          console.log('🎁 [Auto Gift] Gift check result:', {
            giftProductId: giftProductId,
            property: property,
            hasGift: hasGift
          });
          
          return hasGift;
        },

        // Add gift to cart
        async addGiftToCart(productId, quantity, rule) {
          console.log('🎁 [Auto Gift] Adding gift to cart:', {
            productId: productId,
            quantity: quantity,
            rule: rule
          });
          
          try {
            const giftData = {
              id: productId,
              quantity: quantity,
              properties: {
                [rule.property]: rule.propertyValue
              }
            };

            console.log('🎁 [Auto Gift] Sending gift data:', giftData);

            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify(giftData)
            });

            console.log('🎁 [Auto Gift] Add gift response:', {
              ok: response.ok,
              status: response.status,
              statusText: response.statusText
            });

            if (response.ok) {
              console.log('🎁 [Auto Gift] ✅ Free gift added to cart successfully');
              // Trigger cart update to refresh the drawer
              window.dispatchEvent(new CustomEvent('cart:updated'));
            } else {
              const errorText = await response.text();
              console.error('🎁 [Auto Gift] ❌ Failed to add gift:', errorText);
            }
          } catch (error) {
            console.error('🎁 [Auto Gift] ❌ Error adding gift to cart:', error);
          }
        },

        // Remove gift from cart
        async removeGiftFromCart(productId, property) {
          console.log('🎁 [Auto Gift] Removing gift from cart:', {
            productId: productId,
            property: property
          });
          
          try {
            const giftItem = this.cartState.items.find(item => 
              item.variant_id === parseInt(productId) && 
              item.properties && 
              item.properties[property] === 'true'
            );

            console.log('🎁 [Auto Gift] Gift item to remove:', giftItem);

            if (giftItem) {
              const removeData = {
                id: giftItem.key,
                quantity: 0
              };
              
              console.log('🎁 [Auto Gift] Sending remove data:', removeData);

              const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(removeData)
              });

              console.log('🎁 [Auto Gift] Remove gift response:', {
                ok: response.ok,
                status: response.status,
                statusText: response.statusText
              });

              if (response.ok) {
                console.log('🎁 [Auto Gift] ✅ Free gift removed from cart successfully');
                // Trigger cart update to refresh the drawer
                window.dispatchEvent(new CustomEvent('cart:updated'));
              } else {
                const errorText = await response.text();
                console.error('🎁 [Auto Gift] ❌ Failed to remove gift:', errorText);
              }
            } else {
              console.log('🎁 [Auto Gift] No gift item found to remove');
            }
          } catch (error) {
            console.error('🎁 [Auto Gift] ❌ Error removing gift from cart:', error);
          }
        }
      };

      // Listen for cart updates
      window.addEventListener('cart:updated', () => {
        console.log('🎁 [Auto Gift] Cart updated event received');
        if (this.autoGiftManager) {
          this.autoGiftManager.processCartUpdate();
        } else {
          console.log('🎁 [Auto Gift] Auto gift manager not available');
        }
      });
    }

    bindEvents() {
      // Open cart when cart icon is clicked
      if (this.cartToggle) {
        // console.log('Cart toggle button found:', this.cartToggle);
        this.cartToggle.addEventListener('click', (event) => {
          this.openCart(event);
        });
      }

      // Handle add to cart form submissions
      if (this.addToCartForm) {
        this.addToCartForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.addToCart(event);
          this.openCart(event);
        });
      }

      // Close cart when close button is clicked
      if (this.closeButton) {
        document.addEventListener('click', (event) => {
          if (event.target.closest('#cart-drawer-close')) {
            this.closeCart();
          }
        });
      }

      // Close cart when clicking outside
      if (this.cartOverlay) {
        document.addEventListener('click', (event) => {
          if (event.target === this.cartOverlay) {
            this.closeCart();
          }
        });
      }

      // Use event delegation for cart interactions
      document.addEventListener('click', this.handleCartInteractions.bind(this));
      document.addEventListener('change', this.handleCartChanges.bind(this));

      // Handle checkout button
      this.setupCheckoutButton();
     
    
      // Listen for cart open event to refresh recommendations
      document.addEventListener('cart:opened', () => {
        this.setupCarousel();
        this.initializeGiftBoxState();
        this.fetchCart();
      });
    }

    // Fetch the latest cart data and update the UI
    fetchCart() {
      this.showLoading();
      fetch(`${window.Shopify.routes.root}cart.js`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then(cart => {
          // Update cart sections with the latest data
          const sections = this.getSectionsToRender().map(section => section.section).join(',');
          const url = `${window.Shopify.routes.root}?sections=${sections}&sections_url=${window.location.pathname}`;
          
          return fetch(url)
            .then(response => {
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
              return response.json();
            })
            .then(data => {
              this.getSectionsToRender().forEach(section => {
                const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
                if (elementToReplace && data[section.section]) {
                  elementToReplace.innerHTML = new DOMParser()
                    .parseFromString(data[section.section], 'text/html')
                    .querySelector(section.selector).innerHTML;
                }
              });
              setTimeout(() => {
                if (typeof animateCartProgressBars === 'function') animateCartProgressBars();
              }, 0);
            });
        })
        .catch(error => {
          console.error('Error fetching cart:', error);
          // Only show error notification if it's a server error, not a network error
          if (error.message.includes('HTTP error')) {
            this.showErrorNotification('Une erreur est survenue lors du chargement du panier. Veuillez réessayer.');
          }
        })
        .finally(() => {
          this.hideLoading();
        });
    }

    // Handle all click interactions in the cart using event delegation
    handleCartInteractions(event) {
      const target = event.target;
      
      // Handle carousel navigation
      if (target.closest('.cart-drawer__carousel-button')) {
        const button = target.closest('.cart-drawer__carousel-button');
        const track = document.querySelector('.cart-drawer__recommendations-track');
        
        if (!track) return;
        
        if (button.classList.contains('cart-drawer__carousel-prev')) {
          track.scrollBy({
            left: -200,
            behavior: 'smooth'
          });
        } else if (button.classList.contains('cart-drawer__carousel-next')) {
          track.scrollBy({
            left: 200,
            behavior: 'smooth'
          });
        }
        return;
      }
      
      // Handle quantity buttons
      if (target.closest('.cart-drawer__quantity-button')) {
        const button = target.closest('.cart-drawer__quantity-button');
        const input = button.parentNode.querySelector('.cart-drawer__quantity-input');
        const currentValue = parseInt(input.value);
        const lineItemKey = button.closest('.cart-drawer__item')?.dataset.key;
        
        if (!lineItemKey) return;
        
        if (button.dataset.action === 'decrease') {
          if (currentValue === 1) {
            // Remove item from cart
            this.showLoading();
            fetch(`${window.Shopify.routes.root}cart/change.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: lineItemKey,
                quantity: 0,
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: window.location.pathname
              })
            })
            .then(response => response.json())
            .then(state => {
              this.getSectionsToRender().forEach((section => {
                const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
                elementToReplace.innerHTML = this.getSectionInnerHTML(state.sections[section.section], section.selector);
              }));
              // Trigger auto gift manager to check rules
              console.log('🎁 [Auto Gift] Item removed, triggering auto gift check...');
              if (this.autoGiftManager) {
                this.autoGiftManager.processCartUpdate();
              } else {
                console.log('🎁 [Auto Gift] Auto gift manager not available after item removal');
              }
            })
            .catch(error => {
              console.error('Error removing item:', error);
              this.showErrorNotification('Une erreur est survenue lors de la suppression du produit. Veuillez réessayer.');
            })
            .finally(() => {
              this.hideLoading();
              if (typeof animateCartProgressBars === 'function') animateCartProgressBars();
            });
          } else {
            // Decrease quantity
            input.value = currentValue - 1;
            this.updateCartItem(lineItemKey, currentValue - 1);
            // Auto gift manager will handle gift rules automatically
          }
        } else if (button.dataset.action === 'increase') {
          // Increase quantity
          input.value = currentValue + 1;
          this.updateCartItem(lineItemKey, currentValue + 1);
        }
        return;
      }
      
      // Handle remove item buttons (trash icon)
      if (target.closest('.cart-drawer__item-remove')) {
        const button = target.closest('.cart-drawer__item-remove');
        const lineItemKey = button.dataset.key;
        
        if (lineItemKey) {
          // Add a visual indication that the item is being removed
          const cartItem = button.closest('.cart-drawer__item');
          if (cartItem) {
            cartItem.style.opacity = '0.5';
            cartItem.style.pointerEvents = 'none';
          }
          
          // Remove the item from the cart
          this.showLoading();
          fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: lineItemKey,
              quantity: 0,
              sections: this.getSectionsToRender().map((section) => section.section),
              sections_url: window.location.pathname
            })
          })
          .then(response => response.json())
          .then(state => {
            this.getSectionsToRender().forEach((section => {
              const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
              elementToReplace.innerHTML = this.getSectionInnerHTML(state.sections[section.section], section.selector);
            }));
            // Trigger auto gift manager to check rules
            console.log('🎁 [Auto Gift] Item removed via trash button, triggering auto gift check...');
            if (this.autoGiftManager) {
              this.autoGiftManager.processCartUpdate();
            } else {
              console.log('🎁 [Auto Gift] Auto gift manager not available after trash removal');
            }
          })
          .catch(error => {
            console.error('Error removing item:', error);
            this.showErrorNotification('Une erreur est survenue lors de la suppression du produit. Veuillez réessayer.');
          })
          .finally(() => {
            this.hideLoading();
            if (typeof animateCartProgressBars === 'function') animateCartProgressBars();
          });
        }
        return;
      }
      
      
      // Handle recommendation add buttons
      else if (target.closest('.cart-drawer__recommendation-add-button, .cart-drawer__dynamic-recommendation-add-button')) {
        const button = target.closest('.cart-drawer__recommendation-add-button, .cart-drawer__dynamic-recommendation-add-button');
        const variantId = button.dataset.variantId || button.closest('[data-variant-id]')?.dataset.variantId;
        
        if (variantId) {
          this.addToCart(variantId, 1, {});
        }
      }
      
      // Handle bundle toggles
      else if (target.closest('.cart-drawer__bundle-toggle')) {
        const toggle = target.closest('.cart-drawer__bundle-toggle');
        const bundleItems = toggle.nextElementSibling;
        const toggleIcon = toggle.querySelector('.cart-drawer__bundle-toggle-icon');
        
        if (bundleItems.style.display === 'none') {
          // Show bundle items
          bundleItems.style.display = 'block';
          toggleIcon.style.transform = 'rotate(180deg)';
          toggle.innerHTML = toggle.innerHTML.replace('Show', 'Hide');
        } else {
          // Hide bundle items
          bundleItems.style.display = 'none';
          toggleIcon.style.transform = 'rotate(0deg)';
          toggle.innerHTML = toggle.innerHTML.replace('Hide', 'Show');
        }
      }
      
      // Handle gift box container click (if enabled)
      else if (window.cartDrawerSettings?.giftBoxClickable && target.closest('.cart-drawer__gift-box') && !target.closest('.cart-drawer__gift-box-switch')) {
        const giftBoxContainer = target.closest('.cart-drawer__gift-box');
        const giftBoxCheckbox = giftBoxContainer.querySelector('.cart-drawer__gift-box-checkbox');
        
        if (giftBoxCheckbox) {
          giftBoxCheckbox.checked = !giftBoxCheckbox.checked;
          
          // Trigger the change event
          const changeEvent = new Event('change');
          giftBoxCheckbox.dispatchEvent(changeEvent);
        }
      }
    }
    
    // Handle all change events in the cart using event delegation
    handleCartChanges(event) {
      const target = event.target;

      // console.log('handleCartChanges', target);
      
      // Handle quantity input changes
      if (target.matches('.cart-drawer__quantity-input')) {
        const newValue = parseInt(target.value);
        const lineItemKey = target.closest('.cart-drawer__item')?.dataset.key;
        
        if (lineItemKey) {
          if (newValue === 0) {
            // Remove item from cart
            this.showLoading();
            fetch(`${window.Shopify.routes.root}cart/change.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: lineItemKey,
                quantity: 0,
                sections: this.getSectionsToRender().map((section) => section.section),
                sections_url: window.location.pathname
              })
            })
            .then(response => response.json())
            .then(state => {
              this.getSectionsToRender().forEach((section => {
                const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
                elementToReplace.innerHTML = this.getSectionInnerHTML(state.sections[section.section], section.selector);
              }));
            })
            .catch(error => {
              console.error('Error removing item:', error);
              this.showErrorNotification('Une erreur est survenue lors de la suppression du produit. Veuillez réessayer.');
            })
            .finally(() => {
              this.hideLoading();
              if (typeof animateCartProgressBars === 'function') animateCartProgressBars();
            });
          } else if (newValue > 0) {
            // Update quantity
            this.updateCartItem(lineItemKey, newValue);
          } else {
            // Reset to 1 if invalid value
            target.value = 1;
            this.updateCartItem(lineItemKey, 1);
          }
        }
      }
      
      // Handle gift box checkbox changes
      else if (target.matches('.cart-drawer__gift-box-checkbox')) {
        const giftBoxContainer = target.closest('.cart-drawer__gift-box');
        const isChecked = target.checked;
        const giftBoxVariantId = giftBoxContainer?.dataset.variantId;
        
        if (giftBoxContainer && giftBoxVariantId) {
          // Update active class
          if (isChecked) {
            giftBoxContainer.classList.add('active');
          } else {
            giftBoxContainer.classList.remove('active');
          }
          
          // Show loading state
          this.showLoading();
          
          if (isChecked) {
            // Add gift box to cart with _gift_box property
            this.addGiftBoxToCart(giftBoxVariantId);
          } else {
            // Remove gift box from cart
            this.removeGiftBoxFromCart(giftBoxVariantId);
          }
        }
      }
    }

    openCart(event) {
      if (event) event.preventDefault();
      
      if (this.drawer) {
        this.drawer.classList.add('active');
      } 
      
      if (this.cartOverlay) {
        this.cartOverlay.classList.add('active');
      }
      
      document.body.classList.add('cart-drawer-open');
      
      // Dispatch cart opened event
      document.dispatchEvent(new CustomEvent('cart:opened'));

      // Check for auto gifts when drawer opens
      console.log('🎁 [Auto Gift] Cart drawer opened, checking for auto gifts...');
      if (this.autoGiftManager) {
        this.autoGiftManager.processCartUpdate();
      } else {
        console.log('🎁 [Auto Gift] Auto gift manager not available when opening cart');
      }
    }

    closeCart() {
      if (this.drawer) {
        this.drawer.classList.remove('active');
      }
      
      if (this.cartOverlay) {
        this.cartOverlay.classList.remove('active');
      }
      
      document.body.classList.remove('cart-drawer-open');
    }

    setupCheckoutButton() {
      // Use event delegation for checkout button
      document.addEventListener('click', (event) => {
        if (event.target.closest('.cart-drawer__checkout-button')) {
          window.location.href = '/checkout';
        }
      });
    }
    
    // Initialize gift box state based on cart contents
    initializeGiftBoxState() {
      const giftBoxContainer = document.querySelector('.cart-drawer__gift-box');
      if (!giftBoxContainer) return;
      
      const giftBoxCheckbox = giftBoxContainer.querySelector('.cart-drawer__gift-box-checkbox');
      const giftBoxVariantId = giftBoxContainer.dataset.variantId;
      
      if (giftBoxCheckbox && giftBoxVariantId) {
        // Check if gift box is in cart
        this.checkIfGiftBoxInCart(giftBoxVariantId)
          .then(isInCart => {
            // Update checkbox state
            giftBoxCheckbox.checked = isInCart;
            
            // Update active class
            if (isInCart) {
              giftBoxContainer.classList.add('active');
            } else {
              giftBoxContainer.classList.remove('active');
            }
          })
          .catch(error => {
            console.error('Error checking gift box state:', error);
          });
      }
    }

    // Check if gift box is in cart
    checkIfGiftBoxInCart(variantId) {
      // We need to check the actual cart data, not the DOM
      // This will be called after the cart data is fetched
      return fetch(`${window.Shopify.routes.root}cart.js`)
        .then(response => response.json())
        .then(cart => {
          const giftBoxItem = cart.items.find(item => 
            item.variant_id.toString() === variantId.toString() && 
            item.properties && 
            item.properties._gift_box === 'true'
          );
          return !!giftBoxItem;
        })
        .catch(error => {
          console.error('Error checking if gift box is in cart:', error);
          return false;
        });
    }
    
    // Add gift box to cart with _gift_box property
    addGiftBoxToCart(variantId) {
      // First check if the gift box is already in the cart
      fetch(`${window.Shopify.routes.root}cart.js`)
        .then(response => response.json())
        .then(cart => {
          const giftBoxItem = cart.items.find(item => 
            item.variant_id.toString() === variantId.toString() && 
            item.properties && 
            item.properties._gift_box === 'true'
          );
          
          if (giftBoxItem) {
            // Gift box is already in cart, no need to add it again
            this.hideLoading();
            return;
          }
          
          // Add gift box to cart using the generic addToCart method
          this.addToCart(variantId, 1, { _gift_box: 'true' });
        })
        .catch(error => {
          console.error('Error checking cart for gift box:', error);
          this.hideLoading();
          this.showErrorNotification('Une erreur est survenue lors de la vérification du panier. Veuillez réessayer.');
        });
    }
    
    // Remove gift box from cart
    removeGiftBoxFromCart(variantId) {
      // Find the line item key for the gift box
      fetch(`${window.Shopify.routes.root}cart.js`)
        .then(response => response.json())
        .then(cart => {
          const giftBoxItem = cart.items.find(item => 
            item.variant_id.toString() === variantId.toString() && 
            item.properties && 
            item.properties._gift_box === 'true'
          );
          
          if (giftBoxItem) {
            // Remove the gift box using its key
            this.updateCartItem(giftBoxItem.key, 0);
          } else {
            this.hideLoading();
          }
        })
        .catch(error => {
          console.error('Error fetching cart:', error);
          this.hideLoading();
          this.showErrorNotification('Une erreur est survenue lors de la récupération du panier. Veuillez réessayer.');
        });
    }

    updateCartItem(key, quantity) {
      
      // Show loading state
      this.showLoading();
    
      fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      })
      .then(response => {
        return response.text();
      })
      .then(data => {
        let parsedState = JSON.parse(data);
        this.getSectionsToRender().forEach((section => {
          const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        }));
        
        // Trigger auto gift manager to check rules after quantity update
        console.log('🎁 [Auto Gift] Cart item quantity updated, triggering auto gift check...');
        if (this.autoGiftManager) {
          this.autoGiftManager.processCartUpdate();
        } else {
          console.log('🎁 [Auto Gift] Auto gift manager not available after quantity update');
        }
        
        // Hide loading state after update
        this.hideLoading();
        setTimeout(() => {
          if (typeof animateCartProgressBars === 'function') animateCartProgressBars();
        }, 0);
      })
      .catch(error => {
        console.error('Error updating cart item:', error);
        this.hideLoading();
        this.showErrorNotification('Une erreur est survenue lors de la mise à jour du panier. Veuillez réessayer.');
      });
    }
    
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer-cart-drawer',
          section: 'cart-drawer',
          selector: '.cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }

    addToCart(id, quantity, properties = {}) {
      // Show loading state
      this.showLoading();
      
      // Get the cart drawer element and its ID
      const cartDrawerElement = document.querySelector('.cart-drawer');
      const cartDrawerId = cartDrawerElement.id;
      
      const item = {
        id: id,
        quantity: quantity
      };
      
      // Add properties if they exist
      if (Object.keys(properties).length > 0) {
        item.properties = properties;
      }
      
      fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [item],
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      })
      .then(response => response.json())
      .then(data => {
        // Update the sections with the returned HTML
        if (data.sections) {
          this.getSectionsToRender().forEach((section => {
            const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(data.sections[section.section], section.selector);
          }));
        }
        
        // Hide loading state after update
        this.hideLoading();
        setTimeout(() => {
          if (typeof animateCartProgressBars === 'function') animateCartProgressBars();
        }, 0);
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
        this.hideLoading();
        this.showErrorNotification('Une erreur est survenue lors de l\'ajout au panier. Veuillez réessayer.');
      });
    }


    showLoading() {
      // Add loading state to the cart drawer
      if (this.drawer) {
        this.drawer.classList.add('is-loading');
      }
      // Show loading overlay
      if (this.cartOverlay) {
        this.cartOverlay.classList.add('loading');
      }
    }

    hideLoading() {
      // Remove loading state from the cart drawer
      if (this.drawer) {
        this.drawer.classList.remove('is-loading');
      }
      // Hide loading overlay
      if (this.cartOverlay) {
        this.cartOverlay.classList.remove('loading');
      }
    }

    formatMoney(cents) {
      return (cents / 100).toFixed(2).replace('.', ',') + '€';
    }

    showErrorNotification(message) {
      if (this.errorNotification && this.errorMessage) {
        this.errorMessage.textContent = message;
        
        // Show the notification
        this.errorNotification.style.display = 'block';
        
        // Hide after 5 seconds
        clearTimeout(this.notificationTimeout);
        this.notificationTimeout = setTimeout(() => {
          this.errorNotification.style.display = 'none';
        }, 5000);
      }
    }
    
    // Setup carousel for recommendations
    setupCarousel() {
      // Get fresh reference to the track
      this.recommendationsTrack = document.querySelector('.cart-drawer__recommendations-track');
      
      if (!this.recommendationsTrack) {
        // console.log('Recommendations track not found');
        return;
      }
    }

    // Fetch dynamic recommendations
    fetchRecommendations() {
      if (!window.cartDrawerSettings || !window.cartDrawerSettings.enableDynamicRecommendations) {
        console.log('Dynamic recommendations are disabled');
        return;
      }

      if (!this.recommendationsTrack) {
        console.log('Recommendations track element not found');
        return;
      }

      // Get cart data to find product ID
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          if (!cart.items.length) {
            console.log('No cart items found');
            return;
          }

          // Get the first product ID from the cart that doesn't have _goal_product property set to true
          let productId = null;
          for (const item of cart.items) {
            // Skip items that have _goal_product property set to true
            if (!item.properties || !item.properties._goal_product || item.properties._goal_product !== 'true') {
              productId = item.product_id;
              break;
            }
          }
          
          // If no suitable product found, return early
          if (!productId) {
            console.log('No suitable products found for recommendations (all products have _goal_product=true)');
            return;
          }
          const url = `/recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`;
          
          fetch(url)
            .then(response => response.json())
            .then(data => {
              if (data.products && data.products.length > 0) {
                this.renderRecommendations(data.products);
              } else {
                console.log('No recommendations found in response');
              }
            })
            .catch(error => console.error('Error fetching recommendations:', error));
        })
        .catch(error => console.error('Error fetching cart:', error));
    }

    // Render recommendations in the carousel
    renderRecommendations(products) {
      if (!this.recommendationsTrack) return;
      
      this.recommendationsTrack.innerHTML = products.map(product => {
        return `
          <div class="cart-drawer__dynamic-recommendation-item" data-product-id="${product.id}">
            <div class="cart-drawer__dynamic-recommendation-image">
              <img
                src="${product.featured_image ? product.featured_image : ''}"
                alt="${product.title}"
                loading="lazy"
              >
            </div>
            <div class="cart-drawer__dynamic-recommendation-details">
              <h4 class="cart-drawer__dynamic-recommendation-title">${product.title}</h4>
              <div class="cart-drawer__dynamic-recommendation-price">
                ${product.compare_at_price > product.price ? `
                  <span class="cart-drawer__dynamic-recommendation-price-compare">
                    ${this.formatMoney(product.compare_at_price)}
                  </span>
                ` : ''}
                <span class="cart-drawer__dynamic-recommendation-price-final">
                  ${this.formatMoney(product.price)}
                </span>
              </div>
            </div>
            <button class="cart-drawer__dynamic-recommendation-add-button" data-variant-id="${product.variants[0].id}">
              Ajouter
            </button>
          </div>
        `;
      }).join('');
    }

    // Static method to reload cart sections
    static reloadCart() {
      const sectionsToRender = [
        {
          id: 'CartDrawer-cart-drawer',
          section: 'cart-drawer',
          selector: '.cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];

      // Show loading state
      const drawer = document.querySelector('.cart-drawer');
      if (drawer) {
        drawer.classList.add('is-loading');
      }

      const sections = sectionsToRender.map(section => section.section).join(',');
      const url = `${window.Shopify.routes.root}?sections=${sections}&sections_url=${window.location.pathname}`;
      
      fetch(url)
        .then(response => response.json())
        .then(data => {
          sectionsToRender.forEach(section => {
            const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
            if (elementToReplace && data[section.section]) {
              elementToReplace.innerHTML = new DOMParser()
                .parseFromString(data[section.section], 'text/html')
                .querySelector(section.selector).innerHTML;
            }
          });
          setTimeout(() => {
            if (typeof animateCartProgressBars === 'function') animateCartProgressBars();
            if (typeof initCartInfoSlider === 'function') initCartInfoSlider();
          }, 0);
        })
        .catch(error => {
          console.error('Error reloading cart:', error);
        })
        .finally(() => {
          // Hide loading state
          if (drawer) {
            drawer.classList.remove('is-loading');
          }
        });
    }

    // Legacy function removed - now handled by Auto Gift Manager
  };

  // Initialize the cart drawer when the DOM is loaded
  document.addEventListener('DOMContentLoaded', () => {
    console.log('🎁 [Auto Gift] DOM loaded, initializing CartDrawer...');
    new window.CartDrawer();
  });




  // Add initial debug log
  // console.log('Cart discount script loading...');

  // Use both DOMContentLoaded and load events for better Safari compatibility
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDiscount);
  } else {
    initCartDiscount();
  }

  window.addEventListener('load', initCartDiscount);

  function initCartDiscount() {
    // console.log('Initializing cart discount functionality...');

    const cartDrawer = document.querySelector('.cart-drawer');
    if (!cartDrawer) {
      console.error('Cart drawer not found');
      // Try to find it again after a short delay
      setTimeout(() => {
        const retryCartDrawer = document.querySelector('.cart-drawer');
        if (retryCartDrawer) {
          // console.log('Cart drawer found on retry');
          setupEventListeners(retryCartDrawer);
        } else {
          // console.error('Cart drawer still not found after retry');
        }
      }, 1000);
      return;
    }

    setupEventListeners(cartDrawer);
  }

  function setupEventListeners(cartDrawer) {
    // console.log('Setting up event listeners for cart drawer...');

    // Get the error message from the Liquid variable
    const errorElement = cartDrawer.querySelector('.custom-discount-form p.error');
    const defaultErrorMessage =
      errorElement?.textContent || 'An error occurred with the discount code.';

    function setButtonLoading(button, isLoading) {
      if (isLoading) {
        button.classList.add('is-loading');
        button.disabled = true;
      } else {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }

    function clearMessages() {
      const errorElement = cartDrawer.querySelector('.custom-discount-form p.error');
      if (errorElement) {
        // console.log('Clearing error messages');
        errorElement.style.display = 'none';
      }
    }

    function showError() {
      const errorElement = cartDrawer.querySelector('.custom-discount-form p.error');
      if (errorElement) {
        // console.log('Showing error message');
        errorElement.style.display = 'block';
      }
    }

    function getCurrentDiscounts() {
      const discountList = cartDrawer.querySelector('.cart_discounts');
      if (!discountList) return [];

      const discountElements = discountList.querySelectorAll(
        '.discounts__discount span:first-of-type',
      );
      const currentDiscounts = Array.from(discountElements).map(el =>
        el.textContent.trim().toUpperCase(),
      );
      // console.log('Current discounts:', currentDiscounts);
      return currentDiscounts;
    }

    function triggerCartUpdate() {
      // console.log('Triggering cart update...');
      if (typeof CartDrawer !== 'undefined' && CartDrawer.reloadCart) {
        CartDrawer.reloadCart();
      } else {
        console.error('CartDrawer.reloadCart is not available');
        // Fallback: reload the page
        window.location.reload();
      }
    }

    async function validateDiscounts(discountCodes = []) {
      // console.log('Validating discount codes:', discountCodes);
      try {
        const cartRes = await fetch('/cart.js');
        const cartData = await cartRes.json();
        // console.log('Cart data:', cartData);

        const validSet = new Set();
        (cartData.cart_level_discount_applications || []).forEach(d => {
          // console.log('Cart level discount:', d);
          validSet.add(d.title.toUpperCase());
        });

        cartData.items.forEach(item => {
          (item.line_level_discount_allocations || []).forEach(a => {
            // console.log('Line level discount:', a);
            validSet.add(a.discount_application.title.toUpperCase());
          });
        });

        // console.log('Valid discount set:', Array.from(validSet));
        const invalidCodes = discountCodes.filter(c => !validSet.has(c.toUpperCase()));
        // console.log('Invalid codes:', invalidCodes);

        return {
          success: invalidCodes.length === 0,
          invalidCodes,
        };
      } catch (error) {
        console.error('Error validating discounts:', error);
        return {success: false, invalidCodes: []};
      }
    }

    async function applyDiscounts(discountCodes = []) {
      // console.log('Applying discounts:', discountCodes);

      if (discountCodes.length === 0) {
        // console.log('No discount codes, clearing all discounts');
        const targetUrl = '/checkout?discount=CLEAR';
        try {
          const response = await fetch(targetUrl, {method: 'GET', mode: 'no-cors'});
          // console.log('Clear discount response:', response.ok);
          triggerCartUpdate();
          return {success: true};
        } catch (error) {
          console.error('Error clearing discounts:', error);
          return {success: false};
        }
      }

      const discountString = discountCodes.join(',');
      const targetUrl = `/checkout?discount=${encodeURIComponent(discountString)}`;
      // console.log('Applying discounts with URL:', targetUrl);

      try {
        const response = await fetch(targetUrl, {method: 'GET', mode: 'no-cors'});
        // console.log('Apply discount response:', response.ok);

        const validationResult = await validateDiscounts(discountCodes);
        // console.log('Validation result:', validationResult);

        if (validationResult.success) {
          triggerCartUpdate();
        }
        return validationResult;
      } catch (error) {
        console.error('Error applying discounts:', error);
        return {success: false, invalidCodes: [discountCodes[discountCodes.length - 1]]};
      }
    }

    // Setup click event listener
    cartDrawer.addEventListener('click', async function (e) {
      // console.log('Cart drawer click event:', e.target);
      handleInteraction(e, 'click');
    });

    // Setup touch event listener
    cartDrawer.addEventListener('touchstart', async function (e) {
      // console.log('Cart drawer touch event:', e.target);
      handleInteraction(e, 'touch');
    });

    async function handleInteraction(e, type) {
      // Handle apply discount button
      if (e.target.closest('.submit-discount')) {
        const submitButton = e.target.closest('.submit-discount');
        // console.log(`Apply button ${type}ed`);
        e.preventDefault();
        e.stopPropagation();
        clearMessages();

        const discountInput = cartDrawer.querySelector('input[name="custom-discount-input"]');
        if (!discountInput) {
          console.error('Discount input not found');
          return;
        }

        const existingCodes = getCurrentDiscounts();
        let newCode = discountInput.value.trim().toUpperCase();
        // console.log('New discount code:', newCode);
        // console.log('Existing codes:', existingCodes);

        if (!newCode) {
          showError();
          return;
        }

        if (existingCodes.includes(newCode)) {
          showError();
          return;
        }

        // Set loading state
        setButtonLoading(submitButton, true);

        try {
          const combinedCodes = [...existingCodes, newCode];
          // console.log('Attempting to apply combined codes:', combinedCodes);

          const result = await applyDiscounts(combinedCodes);
          // console.log('Apply discount result:', result);

          if (!result.success) {
            showError();
          } else {
            discountInput.value = '';
          }
        } catch (error) {
          console.error('Error applying discount:', error);
          showError();
        } finally {
          // Reset loading state
          setButtonLoading(submitButton, false);
        }
      }

      // Handle remove discount button
      if (e.target.closest('.remove-discount')) {
        const removeButton = e.target.closest('.remove-discount');
        // console.log(`Remove discount button ${type}ed`);

        // Set loading state on the remove button
        removeButton.style.opacity = '0.5';
        removeButton.style.pointerEvents = 'none';

        try {
          const discountCode = removeButton.dataset.code;
          // console.log('Removing discount code:', discountCode);

          const existingCodes = getCurrentDiscounts();
          const remainingCodes = existingCodes.filter(code => code !== discountCode.toUpperCase());
          // console.log('Remaining codes after removal:', remainingCodes);

          const result = await applyDiscounts(remainingCodes);
          // console.log('Remove discount result:', result);

          if (!result.success) {
            showError();
          }
        } catch (error) {
          console.error('Error removing discount:', error);
          showError();
        } finally {
          // Reset loading state
          removeButton.style.opacity = '';
          removeButton.style.pointerEvents = '';
        }
      }
    }

    // console.log('Event delegation setup complete');
  }
  
  // Open cart drawer in Shopify theme editor when the section is selected
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:select', function(evt) {
      // Try to find the cart drawer in the selected section
      var sectionId = evt.detail && evt.detail.sectionId;
      if (!sectionId) return;
      var sectionEl = document.getElementById('shopify-section-' + sectionId);
      if (sectionEl && sectionEl.querySelector('.cart-drawer')) {
        // Ensure CartDrawer instance exists
        if (window.CartDrawer && window.CartDrawer.prototype && window.CartDrawer.prototype.openCart) {
          // Try to find the existing instance
          if (window.cartDrawerInstance && typeof window.cartDrawerInstance.openCart === 'function') {
            window.cartDrawerInstance.openCart();
          } else {
            // Fallback: create a new instance and call openCart
            window.cartDrawerInstance = new window.CartDrawer();
            window.cartDrawerInstance.openCart();
          }
        }
      }
    });
  }

  // Animate progress bars on cart drawer open
  function animateCartProgressBars() {
    setTimeout(function() {
      var bar1 = document.querySelector('.js-cart-progress-bar-1');
      var bar2 = document.querySelector('.js-cart-progress-bar-2');
      var bar3 = document.querySelector('.js-cart-progress-bar-3');
      
      if (bar1) {
        var target1 = bar1.getAttribute('data-progress');
        var parent1 = bar1.closest('.cart-drawer__goal-progress');
        bar1.style.transition = 'none';
        bar1.style.width = '0%';
        void bar1.offsetWidth;
        bar1.style.transition = '';
        bar1.style.width = target1 + '%';
        if (parent1) parent1.classList.remove('filled');
        var onBar1End = function() {
          if (parseFloat(target1) === 100 && parent1) parent1.classList.add('filled');
          bar1.removeEventListener('transitionend', onBar1End);
        };
        bar1.addEventListener('transitionend', onBar1End);
        
        if (bar2) {
          var target2 = bar2.getAttribute('data-progress');
          var parent2 = bar2.closest('.cart-drawer__goal-progress');
          bar2.style.transition = 'none';
          bar2.style.width = '0%';
          void bar2.offsetWidth;
          bar2.style.transition = '';
          bar2.style.width = '0%';
          if (parent2) parent2.classList.remove('filled');
          var animateBar2 = function() {
            bar2.style.width = target2 + '%';
            var onBar2End = function() {
              if (parseFloat(target2) === 100 && parent2) parent2.classList.add('filled');
              bar2.removeEventListener('transitionend', onBar2End);
            };
            bar2.addEventListener('transitionend', onBar2End);
            bar1.removeEventListener('transitionend', animateBar2);
          };
          bar1.addEventListener('transitionend', animateBar2);
          setTimeout(function() {
            var computedWidth = parseFloat(getComputedStyle(bar1).width);
            var targetWidth = bar1.parentNode.offsetWidth * (parseFloat(target1) / 100);
            if (Math.abs(computedWidth - targetWidth) < 2 && bar2.style.width === '0%') {
              animateBar2();
            }
          }, 200);
        }
        
        if (bar3) {
          var target3 = bar3.getAttribute('data-progress');
          var parent3 = bar3.closest('.cart-drawer__goal-progress');
          bar3.style.transition = 'none';
          bar3.style.width = '0%';
          void bar3.offsetWidth;
          bar3.style.transition = '';
          bar3.style.width = '0%';
          if (parent3) parent3.classList.remove('filled');
          var animateBar3 = function() {
            bar3.style.width = target3 + '%';
            var onBar3End = function() {
              if (parseFloat(target3) === 100 && parent3) parent3.classList.add('filled');
              bar3.removeEventListener('transitionend', onBar3End);
            };
            bar3.addEventListener('transitionend', onBar3End);
          };
          
          // Chain bar3 animation after bar2 completes
          if (bar2) {
            var chainBar3 = function() {
              setTimeout(function() {
                var computedWidth = parseFloat(getComputedStyle(bar2).width);
                var targetWidth = bar2.parentNode.offsetWidth * (parseFloat(target2) / 100);
                if (Math.abs(computedWidth - targetWidth) < 2 && bar3.style.width === '0%') {
                  animateBar3();
                }
              }, 200);
            };
            bar2.addEventListener('transitionend', chainBar3);
          } else {
            // If no bar2, animate bar3 after bar1
            setTimeout(function() {
              var computedWidth = parseFloat(getComputedStyle(bar1).width);
              var targetWidth = bar1.parentNode.offsetWidth * (parseFloat(target1) / 100);
              if (Math.abs(computedWidth - targetWidth) < 2 && bar3.style.width === '0%') {
                animateBar3();
              }
            }, 400);
          }
        }
      } else if (bar2) {
        var target2 = bar2.getAttribute('data-progress');
        var parent2 = bar2.closest('.cart-drawer__goal-progress');
        bar2.style.transition = 'none';
        bar2.style.width = '0%';
        void bar2.offsetWidth;
        bar2.style.transition = '';
        bar2.style.width = target2 + '%';
        if (parent2) parent2.classList.remove('filled');
        var onBar2End = function() {
          if (parseFloat(target2) === 100 && parent2) parent2.classList.add('filled');
          bar2.removeEventListener('transitionend', onBar2End);
        };
        bar2.addEventListener('transitionend', onBar2End);
        
        if (bar3) {
          var target3 = bar3.getAttribute('data-progress');
          var parent3 = bar3.closest('.cart-drawer__goal-progress');
          bar3.style.transition = 'none';
          bar3.style.width = '0%';
          void bar3.offsetWidth;
          bar3.style.transition = '';
          bar3.style.width = target3 + '%';
          if (parent3) parent3.classList.remove('filled');
          var onBar3End = function() {
            if (parseFloat(target3) === 100 && parent3) parent3.classList.add('filled');
            bar3.removeEventListener('transitionend', onBar3End);
          };
          bar3.addEventListener('transitionend', onBar3End);
        }
      } else if (bar3) {
        var target3 = bar3.getAttribute('data-progress');
        var parent3 = bar3.closest('.cart-drawer__goal-progress');
        bar3.style.transition = 'none';
        bar3.style.width = '0%';
        void bar3.offsetWidth;
        bar3.style.transition = '';
        bar3.style.width = target3 + '%';
        if (parent3) parent3.classList.remove('filled');
        var onBar3End = function() {
          if (parseFloat(target3) === 100 && parent3) parent3.classList.add('filled');
          bar3.removeEventListener('transitionend', onBar3End);
        };
        bar3.addEventListener('transitionend', onBar3End);
      }
    }, 150);
  }
} 